import React, { useState, useMemo } from 'react';
import { Col, Row } from '../../../lib/utils';
import { colors } from '../../../lib/colors';
import { CaptionLight, Subtitle1Bold, Body2Regular } from '../../../lib/font';
import { useTodayCheckins, useCheckinLogs } from '@/features/checkin/hooks';
import { ICheckinLog } from '@/features/checkin/types';
import dayjs from 'dayjs';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';

type ViewMode = 'today' | 'range';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const CheckinStatsTab = ({ centerId }: { centerId: string }) => {
    const [viewMode, setViewMode] = useState<ViewMode>('today');
    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

    // 오늘 출석 현황 (실시간)
    const { data: todayData, isLoading: todayLoading } = useTodayCheckins({ centerId });

    // 기간별 출석 이력
    const { data: logsData, isLoading: logsLoading } = useCheckinLogs({
        centerId,
        startDate: viewMode === 'range' ? startDate : undefined,
        endDate: viewMode === 'range' ? endDate : undefined,
        limit: 9999,
        enabled: viewMode === 'range',
    });

    const todayCheckins: ICheckinLog[] = todayData?.checkins ?? [];
    const totalToday = todayData?.totalCheckins ?? todayCheckins.length;
    const currentlyIn = todayData?.currentlyIn ?? todayCheckins.filter((c) => !c.checkoutAt).length;

    // 오늘 시간대별 분포 집계
    const hourlyData = useMemo(() => {
        const hours: Record<number, number> = {};
        for (let h = 0; h < 24; h++) hours[h] = 0;
        todayCheckins.forEach((c) => {
            const hour = dayjs(c.checkinAt).hour();
            hours[hour] = (hours[hour] || 0) + 1;
        });
        return Object.entries(hours).map(([hour, count]) => ({
            hour: `${hour}시`,
            count,
            hourNum: Number(hour),
        }));
    }, [todayCheckins]);

    const peakHour = useMemo(() => {
        if (hourlyData.length === 0) return null;
        return hourlyData.reduce((max, cur) => (cur.count > max.count ? cur : max), hourlyData[0]);
    }, [hourlyData]);

    // 기간별 요일 분포 집계
    const rangeCheckins: ICheckinLog[] = logsData?.results ?? [];
    const dayOfWeekData = useMemo(() => {
        const days: number[] = [0, 0, 0, 0, 0, 0, 0];
        rangeCheckins.forEach((c) => {
            const dow = dayjs(c.checkinAt).day();
            days[dow]++;
        });
        return days.map((count, idx) => ({ dayLabel: DAY_LABELS[idx], count, dayOfWeek: idx }));
    }, [rangeCheckins]);

    const uniqueToday = useMemo(() => {
        const set = new Set(todayCheckins.map((c) => c.memberId));
        return set.size;
    }, [todayCheckins]);

    const isLoading = viewMode === 'today' ? todayLoading : logsLoading;

    if (isLoading) {
        return (
            <Col style={{ alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
                <Body2Regular color={colors.LAVEL_1}>데이터를 불러오는 중...</Body2Regular>
            </Col>
        );
    }

    return (
        <Col>
            {/* 필터 */}
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', borderRadius: 8, border: '1px solid #E1E4E6', overflow: 'hidden' }}>
                        {(['today', 'range'] as ViewMode[]).map((mode) => {
                            const isActive = viewMode === mode;
                            return (
                                <button
                                    key={mode}
                                    style={{
                                        padding: '8px 18px',
                                        fontSize: 13,
                                        fontWeight: isActive ? 600 : 400,
                                        backgroundColor: isActive ? '#2563EB' : '#FFFFFF',
                                        color: isActive ? '#FFFFFF' : '#6B7280',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                    }}
                                    onClick={() => setViewMode(mode)}
                                >
                                    {mode === 'today' ? '오늘' : '기간별'}
                                </button>
                            );
                        })}
                    </div>
                    {viewMode === 'range' && (
                        <Row style={{ alignItems: 'center', gap: 6 }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{ border: '1px solid #E1E4E6', borderRadius: 8, padding: '7px 12px', fontSize: 13 }}
                            />
                            <span style={{ color: '#9CA3AF' }}>~</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{ border: '1px solid #E1E4E6', borderRadius: 8, padding: '7px 12px', fontSize: 13 }}
                            />
                        </Row>
                    )}
                </Row>
            </Row>

            {viewMode === 'today' ? (
                <>
                    {/* KPI 카드 - 오늘 */}
                    <Row style={{ marginTop: 24, gap: 16 }}>
                        <KpiCard label="오늘 총 출석" value={totalToday} unit="명" bgColor="#EFF6FF" textColor="#2563EB" />
                        <KpiCard label="현재 이용 중" value={currentlyIn} unit="명" bgColor="#F0FDF4" textColor="#16A34A" />
                        <KpiCard label="순 방문자" value={uniqueToday} unit="명" bgColor="#FFF7ED" textColor="#EA580C" />
                        <KpiCard label="피크 시간" value={peakHour && peakHour.count > 0 ? peakHour.hour : '-'} bgColor="#F5F3FF" textColor="#7C3AED" />
                    </Row>

                    {/* 시간대별 분포 차트 */}
                    <Col style={{ marginTop: 32 }}>
                        <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                            시간대별 출석 분포
                        </Body2Regular>
                        {totalToday > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={hourlyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                    <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} />
                                    <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                        formatter={(value: any) => [`${value}명`, '출석']}
                                    />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={32}>
                                        {hourlyData.map((entry, index) => (
                                            <Cell
                                                key={`hour-${index}`}
                                                fill={peakHour && entry.hourNum === peakHour.hourNum && entry.count > 0 ? '#F59E0B' : '#93C5FD'}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <EmptyState message="오늘 출석 데이터가 없습니다" />
                        )}
                    </Col>
                </>
            ) : (
                <>
                    {/* KPI 카드 - 기간별 */}
                    <Row style={{ marginTop: 24, gap: 16 }}>
                        <KpiCard label="총 출석" value={rangeCheckins.length} unit="건" bgColor="#EFF6FF" textColor="#2563EB" />
                        <KpiCard
                            label="순 방문자"
                            value={new Set(rangeCheckins.map((c) => c.memberId)).size}
                            unit="명"
                            bgColor="#F0FDF4"
                            textColor="#16A34A"
                        />
                    </Row>

                    {/* 요일별 분포 */}
                    <Col style={{ marginTop: 32 }}>
                        <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                            요일별 출석 분포
                        </Body2Regular>
                        {rangeCheckins.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={dayOfWeekData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                    <XAxis dataKey="dayLabel" tick={{ fontSize: 13, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} />
                                    <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                        formatter={(value: any) => [`${value}명`, '출석']}
                                    />
                                    <Bar dataKey="count" fill="#60A5FA" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                        {dayOfWeekData.map((d, index) => (
                                            <Cell key={`day-${index}`} fill={d.dayOfWeek === 0 || d.dayOfWeek === 6 ? '#FDA4AF' : '#60A5FA'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <EmptyState message="해당 기간의 출석 데이터가 없습니다" />
                        )}
                    </Col>
                </>
            )}
        </Col>
    );
};

const KpiCard = ({ label, value, unit, bgColor, textColor }: {
    label: string; value: number | string; unit?: string; bgColor: string; textColor: string;
}) => (
    <Col className="flex-1" style={{ backgroundColor: bgColor, borderRadius: 12, padding: 20 }}>
        <CaptionLight color={colors.LAVEL_1}>{label}</CaptionLight>
        <Subtitle1Bold className="mt-[4px]" style={{ color: textColor }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
            {unit && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 2 }}>{unit}</span>}
        </Subtitle1Bold>
    </Col>
);

const EmptyState = ({ message }: { message: string }) => (
    <Col style={{ alignItems: 'center', justifyContent: 'center', padding: '60px 0', backgroundColor: '#F8F9FA', borderRadius: 12 }}>
        <Body2Regular color={colors.LAVEL_1}>{message}</Body2Regular>
        <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">출석 데이터가 쌓이면 차트가 표시됩니다</CaptionLight>
    </Col>
);

export default CheckinStatsTab;
